import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';

export type NoticeProofZoom = 'fit-page' | 'fit-width' | 50 | 75 | 100;

export interface NoticeProofViewport {
  width: number;
  height: number;
}

interface NoticeProofScaleEntry {
  widthPx: number;
  heightPx: number;
  viewport: NoticeProofViewport | null;
}

/** View-only scale over captured raster dimensions, shared by both comparison sides. */
export function computeNoticeProofScale(
  zoom: NoticeProofZoom,
  entries: readonly NoticeProofScaleEntry[],
): number | null {
  if (
    !entries.length ||
    entries.some(
      (entry) =>
        !Number.isFinite(entry.widthPx) ||
        entry.widthPx <= 0 ||
        !Number.isFinite(entry.heightPx) ||
        entry.heightPx <= 0,
    )
  )
    return null;
  if (zoom === 50 || zoom === 75 || zoom === 100) return zoom / 100;
  if (zoom !== 'fit-page' && zoom !== 'fit-width') return null;
  let scale = 1;
  for (const { widthPx, heightPx, viewport } of entries) {
    if (
      !viewport ||
      !Number.isFinite(viewport.width) ||
      viewport.width <= 0 ||
      !Number.isFinite(viewport.height) ||
      viewport.height <= 0
    )
      return null;
    scale = Math.min(scale, viewport.width / widthPx);
    if (zoom === 'fit-page')
      scale = Math.min(scale, viewport.height / heightPx);
  }
  return Number.isFinite(scale) && scale > 0 ? scale : null;
}

/** Measures the scrollport content box; it never reads or changes source/proof data. */
@Directive({ selector: '[phmsNoticeProofViewport]', standalone: true })
export class NoticeProofViewportDirective implements AfterViewInit, OnDestroy {
  @Output() readonly viewportChange =
    new EventEmitter<NoticeProofViewport | null>();
  private observer?: ResizeObserver;
  private removeResizeListener?: () => void;
  private destroyed = false;
  private queued = false;
  private lastMeasurement: NoticeProofViewport | null | undefined;

  constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.scheduleMeasurement());
        this.observer.observe(this.element.nativeElement);
      } else {
        const window = this.element.nativeElement.ownerDocument.defaultView;
        const listener = (): void => this.scheduleMeasurement();
        window?.addEventListener('resize', listener);
        this.removeResizeListener = () =>
          window?.removeEventListener('resize', listener);
      }
      // Emit after the owning view's initial check, and coalesce resize bursts.
      this.scheduleMeasurement();
    });
  }

  private scheduleMeasurement(): void {
    if (this.destroyed || this.queued) return;
    this.queued = true;
    queueMicrotask(() => {
      this.queued = false;
      if (this.destroyed) return;
      const host = this.element.nativeElement;
      let viewport: NoticeProofViewport | null = null;
      if (host.isConnected && host.clientWidth > 0 && host.clientHeight > 0) {
        const style = host.ownerDocument.defaultView?.getComputedStyle(host);
        const padding = (value: string | undefined): number => {
          const parsed = Number.parseFloat(value ?? '');
          return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
        };
        const width =
          host.clientWidth -
          padding(style?.paddingLeft) -
          padding(style?.paddingRight);
        const height =
          host.clientHeight -
          padding(style?.paddingTop) -
          padding(style?.paddingBottom);
        if (width > 0 && height > 0) viewport = { width, height };
      }
      if (
        this.lastMeasurement !== undefined &&
        this.lastMeasurement?.width === viewport?.width &&
        this.lastMeasurement?.height === viewport?.height
      )
        return;
      this.lastMeasurement = viewport;
      this.zone.run(() => {
        this.viewportChange.emit(viewport);
        this.changeDetector.markForCheck();
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
    this.removeResizeListener?.();
  }
}
